/**
 * The redline engine's plan for a stretch of text: the character edits that
 * turn the source text into the request text, exactly as `RedlineEngine.redline`
 * writes them into a paragraph.
 *
 * One implementation: the engine applies these edits to the paragraph's runs,
 * and every other caller that must lay a change where the engine lays it (the
 * collision reader comparing two cards, a writer working through Word's own
 * tracking) reads the same edits instead of planning its own.
 *
 * The plan, in order:
 *   1. diff the two texts' units (`WordTokenizer.diffUnits`: whitespace words,
 *      a Japanese, Chinese or Thai run cut into its words), compared by
 *      `diffKeyOf`, which reads a unit up to its last letter or digit under the
 *      tokenizer's fold (`WordTokenizer.comparisonKey`), so a word and the same
 *      word under different end punctuation, or retyped with a straight
 *      apostrophe for the document's curly one, are one word;
 *   2. grow each changed hunk into the spacing on either side and trim back the
 *      characters both sides share, never further than that spacing, so a word
 *      is struck and inserted whole and spacing the request keeps is never
 *      struck and written again;
 *   3. inside a run the diff calls equal, write what it still left differing
 *      (`equalRunEdits`): the punctuation at a word's end, then the spacing
 *      between that word and the next.
 *
 * Every comparison of characters is made under the fold (`charactersReadTheSame`):
 * what the fold reads as the same is kept as the document holds it, so a request
 * that retypes "days’" as "days'", a non-breaking space as a space or two spaces
 * as one writes nothing there. A line break is the one whitespace the fold does
 * not hide: the prompt shows it as a line break, so a space in its place is the
 * request's change (`WordTokenizer.whitespaceReadsTheSame`).
 */
import type { PlannedEdit } from "./paragraph-splicer";
import type { AcceptedTextSpan } from "./redline-engine";
import { wordDiff, type WordDiffHunk } from "./text/word-diff";
import { WordTokenizer, type WordToken } from "./text/word-tokenizer";

/** A line break: the one whitespace character the fold must not read as a space when two characters are compared (`charactersReadTheSame`). */
const LINE_BREAK = /[\r\n]/;

/**
 * Whether two characters read the same under the tokenizer's fold, a line break
 * reading as itself alone.
 *
 * EXAMPLE
 *   "’" and "'"        -> true       " " and " " -> true
 *   "\n" and " "       -> false      "e" and "é"      -> false
 */
const charactersReadTheSame = (leftCharacter: string, rightCharacter: string): boolean =>
    WordTokenizer.comparisonKey(leftCharacter) === WordTokenizer.comparisonKey(rightCharacter) && LINE_BREAK.test(leftCharacter) === LINE_BREAK.test(rightCharacter);

/**
 * How many characters an edge two stretches share takes on each side (`sharedEdgeLengths`).
 * The counts differ where a whitespace run of one length reads the same as a run of
 * another: "  " shared with " " is 2 in the source and 1 in the request.
 */
interface SharedEdgeLengths {
    sourceLength: number;
    requestLength: number;
}

/** Whether two runs of end punctuation read the same under the fold: "”" and "\"" do, "." and "," do not. */
const punctuationReadsTheSame = (leftPunctuation: string, rightPunctuation: string): boolean => WordTokenizer.comparisonKey(leftPunctuation) === WordTokenizer.comparisonKey(rightPunctuation);

/** What the plan needs to know about the source text beyond its characters. */
export interface TextEditPlanOptions {
    /**
     * Whether `sourceStartOffset..sourceEndOffset` of the source text is an
     * equation's characters, edited a character at a time.
     *
     * FALLBACK
     *   Left out, every stretch is prose.
     */
    isEquationStretch?: (sourceStartOffset: number, sourceEndOffset: number) => boolean;
}

/**
 * What part of the unit diff a planned edit writes.
 *
 * CASES
 *   - "words"   -> a run of units the request changes, written whole, or the punctuation
 *                  alone at the end of one unit the request otherwise keeps
 *   - "spacing" -> the spacing the request changes between two units it keeps
 */
export type TextEditKind = "words" | "spacing";

/**
 * A planned edit with where its replacement text sits in the request text: the
 * span of the request the edit writes, beside the span of the source it strikes.
 */
export interface PlannedEditWithRequestSpan extends PlannedEdit {
    /**
     * Where `replacementText` sits in the request text, half-open:
     * `requestText.slice(requestStartOffset, requestEndOffset)` is the replacement.
     * The two are equal for a pure deletion, which writes nothing.
     */
    requestStartOffset: number;
    requestEndOffset: number;
}

/**
 * One piece of the change between a source text and a request text, as the
 * plan's edits cut the two (`TextEditPlanner.piecesBetween`): the source is the
 * kept and deleted pieces in order, the request the kept and inserted pieces.
 *
 * CASES
 *   - "kept"     -> a stretch both texts hold, which reads the same under the fold; `text` is
 *                   the source's characters, and the request span may spell its marks and
 *                   spaces otherwise
 *   - "deleted"  -> a stretch of the source the plan strikes; its request span is empty, at
 *                   the offset the deletion sits at in the request
 *   - "inserted" -> a stretch of the request the plan writes; its source span is empty, at the
 *                   offset it is written at in the source, after any deletion there
 */
export interface ChangePiece {
    kind: "kept" | "deleted" | "inserted";
    text: string;
    sourceStartOffset: number;
    sourceEndOffset: number;
    requestStartOffset: number;
    requestEndOffset: number;
}

/**
 * One character edit of the plan, in offsets of the source text, with the units
 * of the diff it comes from, so a caller that compares or writes in units can
 * place it without diffing again.
 */
export interface PlannedTextEdit extends PlannedEditWithRequestSpan {
    kind: TextEditKind;
    /**
     * The source units the edit's change covers, half-open: the hunk's run for a
     * "words" edit over a changed hunk, the one unit whose end punctuation changed
     * for a "words" edit inside an equal run, the empty run at the unit after the
     * gap for a "spacing" edit.
     */
    sourceUnitStart: number;
    sourceUnitEnd: number;
    /** The request units the change writes, half-open, counted the same way. */
    requestUnitStart: number;
    requestUnitEnd: number;
}

export class TextEditPlanner {
    /**
     * Whether the request writes nothing over the source: it reads as the source
     * once spacing is set aside, its words (compared by `WordTokenizer.comparisonKey`)
     * the source's words (`WordTokenizer.readsTheSame`).
     *
     * WHY spacing alone writes nothing
     *   Word rewrites spacing freely, and a request typed by a person or a model never
     *   reproduces a paragraph's tabs and double spaces, so an edit that changes only
     *   spacing is not a change to write. A line break
     *   the request puts between two kept words, or takes away, rides along with a word
     *   change and is written by `editsBetween`.
     *
     * WHY typography alone writes nothing
     *   The fold reads "Supplier's" and "Supplier’s" as one word, so a request that only
     *   retypes the document's marks writes nothing, as a request that retypes its
     *   spacing writes nothing (R13, 2026-10-16).
     *
     * EXAMPLE
     *   "1 Main Street London"   "1 Main Street\nLondon"   -> true: only a line break differs
     *   "days’ notice"           "days' notice"            -> true: only the apostrophe's form differs
     *   "the cat sat"            "the dog sat"             -> false
     *   "本製品の"                 "本製品 の"                -> false: the space splits a word in two
     */
    static requestWritesNothing(sourceText: string, requestText: string): boolean {
        return WordTokenizer.readsTheSame(sourceText, requestText);
    }

    /**
     * The edits that turn `sourceText` into `requestText`, in source order and
     * never overlapping: what the engine strikes and inserts for this request.
     *
     * RETURNS
     *   [] when the request writes nothing (`requestWritesNothing`): the same words,
     *   whatever the spacing.
     *
     * EXAMPLE
     *   source "pay within thirty (30) days"   request "pay within sixty (60) days"
     *   -> [{ kind: "words", startOffset: 11, endOffset: 22, replacementText: "sixty (60)",
     *         sourceUnitStart: 2, sourceUnitEnd: 4, requestUnitStart: 2, requestUnitEnd: 4 }]
     */
    static editsBetween(sourceText: string, requestText: string, options: TextEditPlanOptions = {}): PlannedTextEdit[] {
        /**
         * WHEN the request reads as the source once spacing is set aside
         *   CONSEQUENCE
         *     no edit, as the engine writes none for a spacing-only request
         */
        if (TextEditPlanner.requestWritesNothing(sourceText, requestText)) return [];
        /**
         * FALLBACK
         *   No equation predicate: every stretch is prose, trimmed only as far as its spacing.
         */
        const isEquationStretch = options.isEquationStretch ?? ((): boolean => false);
        /**
         * How many characters two texts share at their start, read under the fold
         * (`charactersReadTheSame`), so a document's non-breaking space or curly mark
         * shared with the request's plain one is left standing.
         */
        const commonPrefixLength = (left: string, right: string): number => {
            let length = 0;
            /**
             * CASES
             *   - `left` is used up  -> stop: all of it is shared
             *   - `right` is used up -> stop
             *   - otherwise          -> continue while the characters at `length` read the same
             */
            const nextPrefixCharacterMatches = (): boolean => {
                const prefixRemainsInLeftText = length < left.length;
                if (!prefixRemainsInLeftText) return false;
                const prefixRemainsInRightText = length < right.length;
                if (!prefixRemainsInRightText) return false;
                return charactersReadTheSame(left[length], right[length]);
            };
            while (nextPrefixCharacterMatches()) length++;
            return length;
        };
        // `limit` caps the count so a suffix can never overlap the prefix already trimmed.
        const commonSuffixLength = (left: string, right: string, limit: number): number => {
            let length = 0;
            /**
             * CASES
             *   - the suffix has reached the cap -> stop: it would overlap the prefix already trimmed
             *   - otherwise                      -> continue while the characters counted from each end read the same
             *
             * EXAMPLE
             *   left = "abcd", length = 0 -> left[4 - 1 - 0] = left[3] = "d", the last character
             *   length = 1               -> left[2] = "c"
             */
            const nextSuffixCharacterMatches = (): boolean => {
                const suffixRemainsWithinLimit = length < limit;
                if (!suffixRemainsWithinLimit) return false;
                return charactersReadTheSame(left[left.length - 1 - length], right[right.length - 1 - length]);
            };
            /**
             * EXAMPLE
             *   left.length  = 8
             *   length       = 0
             *   first check  -> left[7] against right[7]
             *   next check   -> left[6] against right[6]
             *
             * WHY
             *   Compare suffix characters from the end inward.
             *   `limit` stops before the suffix would overlap the prefix already trimmed.
             */
            while (nextSuffixCharacterMatches()) length++;
            return length;
        };
        /**
         * Whether the text just beyond a whitespace run on each side reads the same: the
         * characters there read the same under the fold, or both stretches end there.
         *
         * EXAMPLE, from the end
         *   source "cause.  ", run ending at 8 of length 2 -> beyond is offset `8 - 2` = 6,
         *   the character before it "." (at `6 - 1` = 5); request "discretion. " -> "." too -> true
         */
        const textBeyondRunsReadsTheSame = (sourceStretch: string, requestStretch: string, sourceBeyond: number, requestBeyond: number, fromTheEnd: boolean): boolean => {
            const sourceEnds = fromTheEnd ? sourceBeyond === 0 : sourceBeyond === sourceStretch.length;
            const requestEnds = fromTheEnd ? requestBeyond === 0 : requestBeyond === requestStretch.length;
            /**
             * CASES
             *   - both stretches end beyond their runs -> the same: nothing differs past them
             *   - one does                              -> not the same
             */
            if (sourceEnds || requestEnds) return sourceEnds && requestEnds;
            return fromTheEnd
                ? charactersReadTheSame(sourceStretch[sourceBeyond - 1], requestStretch[requestBeyond - 1])
                : charactersReadTheSame(sourceStretch[sourceBeyond], requestStretch[requestBeyond]);
        };
        /**
         * The next unit a source stretch and a request stretch share at one edge, past
         * what `shared` already counts: one character on each side read under the fold
         * (`charactersReadTheSame`), or, where both edges stand at whitespace runs of
         * different lengths that read the same (`WordTokenizer.whitespaceReadsTheSame`)
         * and the text just beyond both runs reads the same, both runs whole.
         *
         * WHY both runs whole
         *   The fold reads two spaces and one as the same gap, so a request that retypes a
         *   two-space gap with one changes nothing there; compared a character at a time,
         *   the second space stopped the shared edge and the edit struck and retyped what
         *   stood beyond it (T5: "cause.  The" -> "discretion. The" struck "cause. ").
         *
         * WHY only when the text beyond reads the same
         *   Where the text beyond differs, the edge stops at the runs either way, and the
         *   runs are shared a character at a time, as before: a word written into a
         *   two-space gap keeps one of the gap's spaces on each side of it
         *   ("due.  Customer" -> "due. The Customer" writes "The" alone).
         *
         * CASES
         *   - both edges stand at whitespace runs of different lengths that read the same,
         *     with the same text beyond                        -> both runs whole
         *   - either stretch is used up                         -> nothing shared
         *   - otherwise                                         -> one character each, when they read the same
         *
         * NULL
         *   nothing more is shared at this edge.
         */
        const nextSharedEdgeStep = (sourceStretch: string, requestStretch: string, shared: SharedEdgeLengths, fromTheEnd: boolean): SharedEdgeLengths | null => {
            /**
             * EXAMPLE, from the end
             *   sourceStretch "cause.  " (8), shared.sourceLength 0 -> the edge is at `8 - 0` = 8,
             *   the end of the stretch; shared.sourceLength 2 -> at `8 - 2` = 6, the "." before the run
             *
             * EXAMPLE, from the start
             *   shared.sourceLength 1 -> the edge is at offset 1, the character after one shared
             */
            const sourceEdge = fromTheEnd ? sourceStretch.length - shared.sourceLength : shared.sourceLength;
            const requestEdge = fromTheEnd ? requestStretch.length - shared.requestLength : shared.requestLength;
            const sourceWhitespaceRun = fromTheEnd ? WordTokenizer.whitespaceRunLengthEndingAt(sourceStretch, sourceEdge) : WordTokenizer.whitespaceRunLengthStartingAt(sourceStretch, sourceEdge);
            const requestWhitespaceRun = fromTheEnd ? WordTokenizer.whitespaceRunLengthEndingAt(requestStretch, requestEdge) : WordTokenizer.whitespaceRunLengthStartingAt(requestStretch, requestEdge);
            if (sourceWhitespaceRun > 0 && requestWhitespaceRun > 0 && sourceWhitespaceRun !== requestWhitespaceRun) {
                /**
                 * EXAMPLE, from the end
                 *   sourceEdge 8, run 2 -> slice(`8 - 2`, 8) = slice(6, 8), the two spaces;
                 *   beyond them is offset `8 - 2` = 6
                 *
                 * EXAMPLE, from the start
                 *   sourceEdge 0, run 2 -> slice(0, `0 + 2`) = slice(0, 2); beyond is offset `0 + 2` = 2
                 */
                const sourceBeyond = fromTheEnd ? sourceEdge - sourceWhitespaceRun : sourceEdge + sourceWhitespaceRun;
                const requestBeyond = fromTheEnd ? requestEdge - requestWhitespaceRun : requestEdge + requestWhitespaceRun;
                const sourceRun = fromTheEnd ? sourceStretch.slice(sourceBeyond, sourceEdge) : sourceStretch.slice(sourceEdge, sourceBeyond);
                const requestRun = fromTheEnd ? requestStretch.slice(requestBeyond, requestEdge) : requestStretch.slice(requestEdge, requestBeyond);
                const runsAreOneGap = WordTokenizer.whitespaceReadsTheSame(sourceRun, requestRun) && textBeyondRunsReadsTheSame(sourceStretch, requestStretch, sourceBeyond, requestBeyond, fromTheEnd);
                if (runsAreOneGap) return { sourceLength: sourceWhitespaceRun, requestLength: requestWhitespaceRun };
            }
            /** A stretch whose edge has reached its other end has no character left to share. */
            const sourceUsedUp = fromTheEnd ? sourceEdge === 0 : sourceEdge === sourceStretch.length;
            const requestUsedUp = fromTheEnd ? requestEdge === 0 : requestEdge === requestStretch.length;
            if (sourceUsedUp || requestUsedUp) return null;
            /**
             * EXAMPLE, from the end
             *   sourceEdge 6 -> the character at `6 - 1` = 5, the "." just before the edge
             */
            const sourceCharacter = fromTheEnd ? sourceStretch[sourceEdge - 1] : sourceStretch[sourceEdge];
            const requestCharacter = fromTheEnd ? requestStretch[requestEdge - 1] : requestStretch[requestEdge];
            return charactersReadTheSame(sourceCharacter, requestCharacter) ? { sourceLength: 1, requestLength: 1 } : null;
        };
        /**
         * How far a source stretch and a request stretch read the same from their start
         * (`fromTheEnd` false) or their end, counted on each side, unit by unit
         * (`nextSharedEdgeStep`), never past either side's cap.
         *
         * EXAMPLE, from the end, caps 3 and 2
         *   source "cause.  ", request "discretion. "
         *   "  " against " "  -> run against run: source 2, request 1
         *   "." against "."   -> source 3, request 2
         *   "e" against "n"   -> stop
         *   -> { sourceLength: 3, requestLength: 2 }: "cause" is struck, "discretion" written
         */
        const sharedEdgeLengths = (sourceStretch: string, requestStretch: string, sourceCap: number, requestCap: number, fromTheEnd: boolean): SharedEdgeLengths => {
            let shared: SharedEdgeLengths = { sourceLength: 0, requestLength: 0 };
            for (let step = nextSharedEdgeStep(sourceStretch, requestStretch, shared, fromTheEnd); step !== null; step = nextSharedEdgeStep(sourceStretch, requestStretch, shared, fromTheEnd)) {
                const grown: SharedEdgeLengths = { sourceLength: shared.sourceLength + step.sourceLength, requestLength: shared.requestLength + step.requestLength };
                /** A unit that would carry either side past its cap stays with the edit, and so does everything beyond it. */
                if (grown.sourceLength > sourceCap || grown.requestLength > requestCap) break;
                shared = grown;
            }
            return shared;
        };

        interface WordRange {
            firstWordIndex: number;
            endWordIndex: number;
            growIntoLeadingGap: boolean;
            growIntoTrailingGap: boolean;
        }
        /** A character region and how much of the whitespace either side it took, so shared spacing can be trimmed later. */
        interface CharacterRegion {
            span: AcceptedTextSpan;
            leadingGap: number;
            trailingGap: number;
        }

        /**
         * TYPE
         *   Source-side diff units from `sourceText` (`WordTokenizer.diffUnits`): its
         *   words, a Japanese or Chinese run cut into its own words, each with token
         *   text, `startOffset`, and `endOffset` in `sourceText`.
         *
         * WHY diff units and not whitespace runs
         *   A run with no spaces in it would be one unit, so a two-character change
         *   in a Japanese clause struck and rewrote the whole clause.
         */
        const from = WordTokenizer.diffUnits(sourceText);
        /**
         * TYPE
         *   Request-side diff units from `requestText`, cut the same way, each with
         *   token text, `startOffset`, and `endOffset` in `requestText`.
         */
        const to = WordTokenizer.diffUnits(requestText);
        /**
         * The key the diff compares two units by: the unit up to its last letter or
         * digit (`WordTokenizer.splitTrailingPunctuation`), under the tokenizer's fold
         * (`WordTokenizer.comparisonKey`).
         *
         * WHY the punctuation is left out of the key
         *   Compared whole, "sentences" and "sentences;" are two unrelated words, so a
         *   comma added to a word landed in the same hunk as the words added or removed
         *   beside it and the hunk struck the word and typed it again. Left out, the two
         *   are one word to the diff, the words added beside them are their own hunk,
         *   and the punctuation is drawn by `equalRunEdits` where the two words meet.
         *
         * FALLBACK
         *   A unit holding no letter or digit (a dash, a lone bracket) is its own key:
         *   no part of it stays when its characters change.
         *
         * EXAMPLE
         *   "office,"  -> "office"      "office;" -> "office"   (one key, so one word)
         *   "party's"  -> "party's"     "party’s" -> "party's"  (one key: the apostrophe folds)
         *   "—"        -> "-"
         */
        const diffKeyOf = (unitText: string): string => {
            const unitParts = WordTokenizer.splitTrailingPunctuation(unitText);
            if (unitParts === null) return WordTokenizer.comparisonKey(unitText);
            return WordTokenizer.comparisonKey(unitParts.textBeforeTrailingPunctuation);
        };
        const ops = wordDiff(
            from.map((token) => diffKeyOf(token.text)),
            to.map((token) => diffKeyOf(token.text)),
        );

        // The character region covered by a word range, grown into the whitespace
        // on either side when asked.
        const regionOf = (text: string, tokens: WordToken[], range: WordRange): CharacterRegion => {
            /**
             * CASES
             *   - range starts on a token -> use that token's `startOffset`
             *   - range starts after all tokens -> use `text.length`
             */
            const tokenStart = range.firstWordIndex < tokens.length ? tokens[range.firstWordIndex].startOffset : text.length;
            /**
             * CASES
             *   - non-empty range -> end at the last token's `endOffset`
             *   - empty range     -> fall back to `tokenStart`
             *
             * WHY
             *   `endWordIndex` is exclusive.
             */
            const tokenEnd = range.firstWordIndex < range.endWordIndex ? tokens[range.endWordIndex - 1].endOffset : tokenStart;
            const rangeMayGrowIntoLeadingGap = range.growIntoLeadingGap;
            const rangeHasPreviousToken = range.firstWordIndex > 0;
            const rangeUsesPreviousTokenEnd = rangeMayGrowIntoLeadingGap && rangeHasPreviousToken;
            /**
             * CASES
             *   - may grow left and previous token exists -> use that token's `endOffset`
             *   - otherwise                               -> use `tokenStart`
             */
            const startOffset = rangeUsesPreviousTokenEnd ? tokens[range.firstWordIndex - 1].endOffset : tokenStart;
            const rangeMayGrowIntoTrailingGap = range.growIntoTrailingGap;
            const rangeHasNextToken = range.endWordIndex < tokens.length;
            const rangeUsesNextTokenStart = rangeMayGrowIntoTrailingGap && rangeHasNextToken;
            /**
             * CASES
             *   - may grow right and next token exists -> use that token's `startOffset`
             *   - otherwise                            -> use `tokenEnd`
             */
            const endOffset = rangeUsesNextTokenStart ? tokens[range.endWordIndex].startOffset : tokenEnd;
            const rangeHoldsNoToken = range.firstWordIndex === range.endWordIndex;
            /**
             * CASES, for how much of the region is spacing at each end
             *   - the range holds tokens   -> what it grew into: tokenStart - startOffset on the
             *                                 left, endOffset - tokenEnd on the right
             *   - the range holds no token -> all of it at both ends: the request's side of a
             *                                 deletion, the paragraph's side of an insertion
             *
             * WHY both ends of a region with no token
             *   Its tokenStart and tokenEnd both sit at its right edge, so its spacing read
             *   as leading only and a space it shares with the other side at the end could
             *   not be trimmed: deleting "の" from "本製品の 保証期間" struck "の " and
             *   wrote " " back.
             *
             * EXAMPLE
             *   request "本製品 保証期間", deletion before "保証" (startOffset 4)
             *   region 3..4 = " "  -> leadingGap 1, trailingGap 1 (was 0)
             */
            const leadingGap = rangeHoldsNoToken ? endOffset - startOffset : tokenStart - startOffset;
            const trailingGap = rangeHoldsNoToken ? endOffset - startOffset : endOffset - tokenEnd;
            return { span: { startOffset, endOffset }, leadingGap, trailingGap };
        };
        // Trimming of the shared prefix and suffix is capped at the whitespace
        // gaps on purpose: replacing "diligent" with "reasonable" must strike and
        // insert whole words, never share the "d" or the "e" between them. The cap
        // is lifted inside an equation, where the unit of an edit is the character.
        const hunkEdit = (index: number): PlannedEditWithRequestSpan => {
            /**
             * TRUE MEANS
             *   This hunk may borrow whitespace from the gap on its left because an `equal`
             *   run sits immediately before it.
             */
            const growIntoLeadingGap = index > 0 && ops[index - 1].tag === "equal";
            /**
             * TRUE MEANS
             *   This hunk may borrow whitespace from the gap on its right because an `equal`
             *   run sits immediately after it.
             */
            const growIntoTrailingGap = index + 1 < ops.length && ops[index + 1].tag === "equal";
            return unitRangeEdit(ops[index], growIntoLeadingGap, growIntoTrailingGap);
        };
        /**
         * The edit that turns one range of source units into one range of request
         * units: a whole hunk, or the part of a hunk that one sentence holds.
         *
         * `growIntoLeadingGap` and `growIntoTrailingGap` say whether the range may
         * borrow the spacing on either side, which it may where a run of kept words
         * meets it and the spacing is shared with what is kept.
         */
        /**
         * How many characters of sentence-ending punctuation the range's last source
         * word and last request word share exactly, which the edit then leaves standing.
         *
         * WHY
         *   The edit's shared suffix is trimmed only as far as the whitespace after its
         *   last word, so a changed last word took its unchanged end mark with it:
         *   "cause." -> "discretion." struck "cause." and retyped the ".", and the
         *   sentence's period read as changed (R41, QA steps 7 and 8).
         *
         * CASES
         *   - both ranges end on a word with the same punctuation, and it ends a
         *     sentence (`WordTokenizer.endsSentence`)                   -> its length
         *   - the same punctuation that ends no sentence (a comma, a closing bracket)
         *                                                              -> 0: a word's own
         *     mark inside a sentence goes with the word, as "(30)" -> "(60)" always has
         *   - the punctuation differs, either side ends on a unit with no letter or
         *     digit, or either range is empty                          -> 0: today's trim
         *
         * EXAMPLE
         *   "days."  -> "Days."       -> 1
         *   "hour."  -> "minutes."    -> 1
         *   "days)." -> "Days)."      -> 2
         *   "(30)"   -> "(60)"        -> 0
         *   "days."  -> "days;"       -> 0
         */
        const endMarkBothLastUnitsKeepLengths = (op: Omit<WordDiffHunk, "tag">): SharedEdgeLengths => {
            const noEndMarkKept: SharedEdgeLengths = { sourceLength: 0, requestLength: 0 };
            /** A pure insertion or deletion has no last word on one side to share a mark with. */
            if (op.beforeStart === op.beforeEnd || op.afterStart === op.afterEnd) return noEndMarkKept;
            /**
             * EXAMPLE
             *   op.beforeEnd = 7 -> 7 - 1 = 6, the range's last source word (the end is exclusive)
             */
            const lastSourceUnitParts = WordTokenizer.splitTrailingPunctuation(from[op.beforeEnd - 1].text);
            const lastRequestUnitParts = WordTokenizer.splitTrailingPunctuation(to[op.afterEnd - 1].text);
            /** A last unit with no letter or digit (a dash, a lone bracket) has no word to keep a mark after. */
            if (lastSourceUnitParts === null || lastRequestUnitParts === null) return noEndMarkKept;
            /** A mark the request changes is the edit's to write; one it retypes in another form is the same mark. */
            if (!punctuationReadsTheSame(lastSourceUnitParts.trailingPunctuation, lastRequestUnitParts.trailingPunctuation)) return noEndMarkKept;
            /** A mark that ends no sentence belongs to the changed word, and goes with it. */
            if (!WordTokenizer.endsSentence(lastSourceUnitParts.trailingPunctuation)) return noEndMarkKept;
            return { sourceLength: lastSourceUnitParts.trailingPunctuation.length, requestLength: lastRequestUnitParts.trailingPunctuation.length };
        };
        const unitRangeEdit = (op: Omit<WordDiffHunk, "tag">, growIntoLeadingGap: boolean, growIntoTrailingGap: boolean): PlannedEditWithRequestSpan => {
            const cut = regionOf(sourceText, from, { firstWordIndex: op.beforeStart, endWordIndex: op.beforeEnd, growIntoLeadingGap, growIntoTrailingGap });
            const added = regionOf(requestText, to, { firstWordIndex: op.afterStart, endWordIndex: op.afterEnd, growIntoLeadingGap, growIntoTrailingGap });
            /**
             * EXAMPLE
             *   cut.span.startOffset = 12
             *   cut.span.endOffset   = 17
             *   slice(12, 17)        -> characters 12 through 16
             *
             * WHY
             *   Take the exact accepted-text region this hunk would cut before trimming.
             */
            const cutText = sourceText.slice(cut.span.startOffset, cut.span.endOffset);
            /**
             * EXAMPLE
             *   added.span.startOffset = 4
             *   added.span.endOffset   = 9
             *   slice(4, 9)            -> characters 4 through 8
             *
             * WHY
             *   Take the matching request-side region before trimming shared edges.
             */
            const addedText = requestText.slice(added.span.startOffset, added.span.endOffset);
            /**
             * TYPE
             *   Character count of the shared leading text trimmed from both sides, capped
             *   by the borrowed leading gap on each side.
             */
            const cutsEquationCharacters = isEquationStretch(cut.span.startOffset, cut.span.endOffset);
            /**
             * CASES, the shared start each side may trim (`sharedEdgeLengths`, a whitespace run one unit)
             *   - the cut is prose          -> only as far as the whitespace each side borrowed
             *   - the cut is in an equation -> the whole shared start, down to the character
             */
            const prefix = sharedEdgeLengths(
                cutText,
                addedText,
                cutsEquationCharacters ? cutText.length : cut.leadingGap,
                cutsEquationCharacters ? addedText.length : added.leadingGap,
                false,
            );
            const keptEndMark = endMarkBothLastUnitsKeepLengths(op);
            /**
             * CASES, the shared end each side may trim, never into the start already trimmed
             *   - the cut is prose          -> as far as the whitespace each side borrowed, and past
             *                                  it the end mark both last words share
             *   - the cut is in an equation -> the whole shared end, down to the character
             *
             * EXAMPLE, "within thirty (30) days." -> "within five (5) Business Days."
             *   cut "thirty (30) days.", added "five (5) Business Days.", no gap after either
             *   caps `0 + 1` and `0 + 1`: the shared end stops after the "." (1 each)
             *   edit [-thirty (30) days][+five (5) Business Days]: the "." stays, never retyped
             *
             * EXAMPLE, "cause.  The" -> "discretion. The" (T5)
             *   cut "cause.  " (gap 2), added "discretion. " (gap 1), end mark "." on each side
             *   caps `2 + 1` = 3 and `1 + 1` = 2: shared end 3 and 2, the run and the "."
             *   edit [-cause][+discretion]: the "." and the paragraph's two spaces stay
             *
             * EXAMPLE, the start already trimmed
             *   cutText.length 8, prefix.sourceLength 1 -> `8 - 1` = 7 characters left to share at the end
             */
            const suffix = sharedEdgeLengths(
                cutText,
                addedText,
                Math.min(cutText.length - prefix.sourceLength, cutsEquationCharacters ? cutText.length : cut.trailingGap + keptEndMark.sourceLength),
                Math.min(addedText.length - prefix.requestLength, cutsEquationCharacters ? addedText.length : added.trailingGap + keptEndMark.requestLength),
                true,
            );
            return {
                /**
                 * EXAMPLE
                 *   cut.span.startOffset = 12
                 *   prefix.sourceLength  = 2
                 *   trimmed startOffset  = 14
                 */
                startOffset: cut.span.startOffset + prefix.sourceLength,
                /**
                 * EXAMPLE
                 *   cut.span.endOffset  = 20
                 *   suffix.sourceLength = 3
                 *   trimmed endOffset   = 17
                 *
                 * WHY
                 *   The end stays exclusive after trimming the shared suffix.
                 */
                endOffset: cut.span.endOffset - suffix.sourceLength,
                /**
                 * EXAMPLE
                 *   addedText.length     = 12 ("discretion. ")
                 *   prefix.requestLength = 0
                 *   suffix.requestLength = 2 (". ")
                 *   slice(0, `12 - 2`)   -> "discretion", the changed middle
                 *
                 * WHY
                 *   Drop the shared prefix and suffix so only the changed middle is inserted.
                 */
                replacementText: addedText.slice(prefix.requestLength, addedText.length - suffix.requestLength),
                /**
                 * EXAMPLE
                 *   added.span = 4..14, prefix.requestLength 2, suffix.requestLength 3
                 *   -> the replacement sits at `4 + 2` = 6 to `14 - 3` = 11 of the request
                 */
                requestStartOffset: added.span.startOffset + prefix.requestLength,
                requestEndOffset: added.span.endOffset - suffix.requestLength,
            };
        };
        /**
         * One word of an equal run drawn as a "words" edit of its own, tagged with
         * that single word on each side.
         *
         * WHY one word and not the run
         *   The run's other words are unchanged, and a caller that reads the plan in
         *   units (the collision reader, the card editor) must see only the word whose
         *   characters the request changes.
         */
        // Inside an equal run the words read the same up to their last letter or digit,
        // but the punctuation after it and the spacing between them may still differ
        // (a comma added, a double space, a tab, a line break); each is its own edit,
        // emitted in source order: a word's punctuation, then the gap after that word.
        const equalRunEdits = (op: WordDiffHunk, lastWordPunctuationJoinsNextHunk: boolean): PlannedTextEdit[] => {
            const edits: PlannedTextEdit[] = [];
            const runWordCount = op.beforeEnd - op.beforeStart;
            /**
             * EXAMPLE
             *   op.beforeStart = 2, runWordCount = 3
             *   runOffset 0, 1, 2 -> the run's words at from[2], from[3], from[4]
             *   the gap after each -> (2, 3), (3, 4), and none after the last
             */
            for (let runOffset = 0; runOffset < runWordCount; runOffset++) {
                const sourceWordIndex = op.beforeStart + runOffset;
                const requestWordIndex = op.afterStart + runOffset;
                const sourceWord = from[sourceWordIndex];
                const requestWord = to[requestWordIndex];
                /**
                 * EXAMPLE
                 *   runWordCount = 3, runOffset 2 -> 2 + 1 = 3: the run's last word
                 */
                const isRunsLastWord = runOffset + 1 === runWordCount;
                /**
                 * CASES
                 *   - the run's last word, whose punctuation moves across the next hunk
                 *     (`wordBeforeMovedPunctuation`, `deletedWordsBeforeKeptPunctuation`)
                 *     -> no edit here: the next hunk's edit writes the words around the
                 *     punctuation and leaves it standing
                 *   - any other word -> its end punctuation as the request spells it
                 */
                const punctuationEdit = isRunsLastWord && lastWordPunctuationJoinsNextHunk ? null : trailingPunctuationEdit(sourceWord, requestWord);
                /**
                 * WHEN the request changes the punctuation at this word's end
                 *   CONSEQUENCE
                 *     that punctuation alone is struck and inserted, tagged with this one
                 *     word on each side, before the gap that follows it.
                 */
                if (punctuationEdit !== null) {
                    edits.push(wordsEditOf({ beforeStart: sourceWordIndex, beforeEnd: sourceWordIndex + 1, afterStart: requestWordIndex, afterEnd: requestWordIndex + 1 }, punctuationEdit));
                }
                /**
                 * WHEN this is the run's last word
                 *   CONSEQUENCE
                 *     no gap of the run follows it; the next hunk's own edit covers whatever
                 *     stands after it.
                 */
                if (isRunsLastWord) continue;
                /**
                 * EXAMPLE
                 *   sourceWordIndex = 3 -> 4, the word right after the gap
                 */
                const rightSourceWordIndex = sourceWordIndex + 1;
                const right = from[rightSourceWordIndex];
                /**
                 * EXAMPLE
                 *   sourceWord.endOffset = 8
                 *   right.startOffset    = 11
                 *   slice(8, 11)         -> 3-character separator
                 *
                 * WHY
                 *   Take the source-side text between two equal words.
                 */
                const live = sourceText.slice(sourceWord.endOffset, right.startOffset);
                const wantedSeparatorStartOffset = requestWord.endOffset;
                const rightRequestWordIndex = requestWordIndex + 1;
                const rightRequestWord = to[rightRequestWordIndex];
                const wantedSeparatorEndOffset = rightRequestWord.startOffset;
                /**
                 * EXAMPLE
                 *   requestWordIndex = 6
                 *   separator        -> gap after `to[6]` and before `to[7]`
                 *
                 * WHY
                 *   Take the request-side separator for the matching word pair.
                 */
                const wanted = requestText.slice(wantedSeparatorStartOffset, wantedSeparatorEndOffset);
                /**
                 * WHEN the two separators read the same to the model: both or neither hold a
                 *      line break, both or neither are empty (`WordTokenizer.whitespaceReadsTheSame`)
                 *   CONSEQUENCE
                 *     no edit: the paragraph's own run stays, however the request retyped it
                 *     (two spaces as one, a non-breaking space as a space, a tab as a space).
                 */
                if (WordTokenizer.whitespaceReadsTheSame(live, wanted)) continue;
                /**
                 * TYPE
                 *   Character count of the shared leading separator text already equal on both
                 *   sides.
                 */
                const prefix = commonPrefixLength(live, wanted);
                const shorterSeparatorLength = Math.min(live.length, wanted.length);
                const suffixOverlapLimit = shorterSeparatorLength - prefix;
                /**
                 * TYPE
                 *   Character count of the shared trailing separator text, limited so it cannot
                 *   overlap the prefix already trimmed.
                 */
                const suffix = commonSuffixLength(live, wanted, suffixOverlapLimit);
                edits.push({
                    kind: "spacing",
                    /**
                     * WHY the empty runs at the words after the gap
                     *   A spacing edit covers and writes no unit: it sits in front of the
                     *   word right after the gap on each side.
                     */
                    sourceUnitStart: rightSourceWordIndex,
                    sourceUnitEnd: rightSourceWordIndex,
                    requestUnitStart: rightRequestWordIndex,
                    requestUnitEnd: rightRequestWordIndex,
                    /**
                     * EXAMPLE
                     *   source separator = sourceText[8..12)
                     *   prefix           = 1
                     *   startOffset      = 9
                     */
                    startOffset: sourceWord.endOffset + prefix,
                    /**
                     * EXAMPLE
                     *   right.startOffset = 12
                     *   suffix            = 2
                     *   endOffset         = 10
                     *
                     * WHY
                     *   The end stays exclusive after trimming the shared suffix.
                     */
                    endOffset: right.startOffset - suffix,
                    /**
                     * EXAMPLE
                     *   wanted.length = 4
                     *   prefix        = 1
                     *   suffix        = 1
                     *   slice(1, 3)   -> differing middle only
                     *
                     * WHY
                     *   For a spacing-only edit, leave out the shared outer characters.
                     */
                    replacementText: wanted.slice(prefix, wanted.length - suffix),
                    /**
                     * EXAMPLE
                     *   the request's separator sits at 20..24, prefix 1, suffix 1 -> the replacement at 21..23
                     */
                    requestStartOffset: wantedSeparatorStartOffset + prefix,
                    requestEndOffset: wantedSeparatorEndOffset - suffix,
                });
            }
            return edits;
        };
        /**
         * The edit that changes the punctuation at the ends of one pair of words the
         * diff matched, and leaves the rest of both words standing.
         *
         * WHEN the two words of a pair of an equal run do not read the same
         *   CONSEQUENCE
         *     they read the same up to their last letter or digit, since that is what
         *     `diffKeyOf` matched them on, so the only thing left to draw is the
         *     punctuation at their ends
         *
         * EXAMPLE
         *   source "at its principal office, (b)"   request "at its principal office; (b)"
         *   pair   "office," -> "office;"
         *   edit   offsets of "," in the source, replacement ";"   (office[-,-]{+;+})
         *
         * NULL
         *   the pair is one and the same word, or neither word holds a letter or digit:
         *   there is no punctuation change to draw.
         */
        const trailingPunctuationEdit = (sourceWord: WordToken, requestWord: WordToken): PlannedEditWithRequestSpan | null => {
            const sourceWordParts = WordTokenizer.splitTrailingPunctuation(sourceWord.text);
            const requestWordParts = WordTokenizer.splitTrailingPunctuation(requestWord.text);
            if (sourceWordParts === null || requestWordParts === null) return null;
            const sourcePunctuation = sourceWordParts.trailingPunctuation;
            const requestPunctuation = requestWordParts.trailingPunctuation;
            /** A mark retyped in another form ("”" for "\"") is the same mark under the fold: nothing to draw. */
            if (punctuationReadsTheSame(sourcePunctuation, requestPunctuation)) return null;
            const sharedPunctuationPrefix = commonPrefixLength(sourcePunctuation, requestPunctuation);
            /**
             * EXAMPLE
             *   sourcePunctuation = "...", requestPunctuation = "."
             *   shared prefix 1; shorter length 1 - 1 = 0 caps the suffix at 0
             */
            const sharedPunctuationSuffix = commonSuffixLength(sourcePunctuation, requestPunctuation, Math.min(sourcePunctuation.length, requestPunctuation.length) - sharedPunctuationPrefix);
            /**
             * EXAMPLE
             *   sourceWord "claim..." at startOffset 40, textBeforeTrailingPunctuation "claim" (5)
             *   punctuation starts at 40 + 5 = 45
             */
            const sourcePunctuationStart = sourceWord.startOffset + sourceWordParts.textBeforeTrailingPunctuation.length;
            /**
             * EXAMPLE
             *   requestWord "office;" at startOffset 40, textBeforeTrailingPunctuation "office" (6)
             *   its punctuation starts at 40 + 6 = 46
             */
            const requestPunctuationStart = requestWord.startOffset + requestWordParts.textBeforeTrailingPunctuation.length;
            return {
                /**
                 * EXAMPLE
                 *   sourcePunctuationStart = 45, shared prefix 1 -> 46: the first "." stays
                 */
                startOffset: sourcePunctuationStart + sharedPunctuationPrefix,
                /**
                 * EXAMPLE
                 *   sourceWord.endOffset = 48, shared suffix 0 -> 48: the edit strikes ".." at 46..48
                 */
                endOffset: sourceWord.endOffset - sharedPunctuationSuffix,
                /**
                 * EXAMPLE
                 *   requestPunctuation = ".", shared prefix 1, shared suffix 0
                 *   slice(1, 1) -> "": nothing is inserted, claim.[-..-]
                 */
                replacementText: requestPunctuation.slice(sharedPunctuationPrefix, requestPunctuation.length - sharedPunctuationSuffix),
                /**
                 * EXAMPLE
                 *   request punctuation at 46..47, shared prefix 0, shared suffix 0 -> the ";" at 46..47
                 */
                requestStartOffset: requestPunctuationStart + sharedPunctuationPrefix,
                requestEndOffset: requestWord.endOffset - sharedPunctuationSuffix,
            };
        };
        /**
         * An edit that writes units, tagged with the units it covers on both sides: a
         * changed hunk's whole run, or the one word of an equal run whose end
         * punctuation the request changes.
         */
        const wordsEditOf = (units: Omit<WordDiffHunk, "tag">, edit: PlannedEditWithRequestSpan): PlannedTextEdit => ({
            ...edit,
            kind: "words",
            sourceUnitStart: units.beforeStart,
            sourceUnitEnd: units.beforeEnd,
            requestUnitStart: units.afterStart,
            requestUnitEnd: units.afterEnd,
        });

        /**
         * A kept word whose end punctuation the request moves past the words it
         * inserts right after that word: how long the word's text before that
         * punctuation is, in the source and in the request.
         */
        interface WordBeforeMovedPunctuation {
            sourceTextBeforePunctuationLength: number;
            requestTextBeforePunctuationLength: number;
        }
        /**
         * The equal run at `equalOpIndex`'s last word, when the request moves that
         * word's end punctuation past the words it inserts right after it.
         *
         * WHEN the next hunk only inserts words, the run's last word loses its end
         * punctuation in the request, and the last inserted word ends with that same
         * punctuation
         *   CONSEQUENCE
         *     the word: the punctuation never changed, the request wrote words in front
         *     of it. Drawn as a punctuation edit plus an insertion, the redline struck the
         *     period the sentence keeps and typed it again after the new words, and the
         *     new words landed past the period, outside a link the word sat in.
         *
         * EXAMPLE
         *   source "in the Fee Schedule."    request "in Schedule 1 (Fees)."
         *   last word of the run   "Schedule." -> "Schedule"   loses "."
         *   inserted words          "1", "(Fees)."              the last ends with ")." , which ends with "."  -> the word
         *   source "the Agreement."           request "the Agreement; and"
         *   "Agreement." -> "Agreement;", inserted "and" ends with no punctuation          -> null
         *
         * NULL
         *   the hunk at `equalOpIndex` is not an equal run, no pure insertion follows it,
         *   or the punctuation really changes.
         */
        const wordBeforeMovedPunctuation = (equalOpIndex: number): WordBeforeMovedPunctuation | null => {
            const equalOp = ops[equalOpIndex];
            /**
             * EXAMPLE
             *   equalOpIndex = 2 -> 2 + 1 = 3, the hunk right after the run
             */
            const nextOp = ops[equalOpIndex + 1];
            /**
             * WHEN the hunk is not an equal run, the run is the last hunk, or the next
             * hunk strikes source words
             *   CONSEQUENCE
             *     null: there is no pure insertion right after a kept word.
             */
            if (equalOp.tag !== "equal" || nextOp === undefined || nextOp.tag !== "insert") return null;
            /**
             * EXAMPLE
             *   "in the Fee Schedule." -> "in Schedule 1 (Fees).", the run ["Schedule."]
             *   equalOp.beforeEnd = 4 -> 4 - 1 = 3, "Schedule.", the run's last source word
             *   equalOp.afterEnd  = 2 -> 2 - 1 = 1, "Schedule", the request word it was matched with
             */
            const sourceWordParts = WordTokenizer.splitTrailingPunctuation(from[equalOp.beforeEnd - 1].text);
            const requestWordParts = WordTokenizer.splitTrailingPunctuation(to[equalOp.afterEnd - 1].text);
            /**
             * EXAMPLE
             *   nextOp.afterEnd = 4 -> 4 - 1 = 3, "(Fees).", the last word the request inserts
             */
            const lastInsertedWordParts = WordTokenizer.splitTrailingPunctuation(to[nextOp.afterEnd - 1].text);
            /**
             * WHEN a word holds no letter or digit (a dash, a lone bracket)
             *   CONSEQUENCE
             *     null: it has no end punctuation to keep apart from its text.
             */
            if (sourceWordParts === null || requestWordParts === null || lastInsertedWordParts === null) return null;
            const keptPunctuation = sourceWordParts.trailingPunctuation;
            /**
             * CASES, every mark read under the fold
             *   - the source word has no end punctuation          -> null: nothing to keep
             *   - the request word keeps it as it is              -> null: no punctuation edit to fold
             *   - the last inserted word ends with something else -> null: the punctuation really changed
             */
            if (keptPunctuation.length === 0) return null;
            if (punctuationReadsTheSame(requestWordParts.trailingPunctuation, keptPunctuation)) return null;
            if (!WordTokenizer.comparisonKey(lastInsertedWordParts.trailingPunctuation).endsWith(WordTokenizer.comparisonKey(keptPunctuation))) return null;
            return {
                sourceTextBeforePunctuationLength: sourceWordParts.textBeforeTrailingPunctuation.length,
                requestTextBeforePunctuationLength: requestWordParts.textBeforeTrailingPunctuation.length,
            };
        };
        /**
         * The one edit for a kept word's moved punctuation and the words inserted after
         * it (`wordBeforeMovedPunctuation`): the stretch from the word's punctuation
         * to the next source word, rewritten as the request spells it, with the
         * characters both sides share at either end left standing.
         *
         * WHY sharing never cuts a word
         *   The source side of the stretch is the word's punctuation and the spacing
         *   after it, so only punctuation and spacing can be shared and left standing.
         *
         * EXAMPLE
         *   source "in the Fee Schedule."   "Schedule." at 11..20, punctuation at 19
         *   request "in Schedule 1 (Fees)." "Schedule" at 3..11, next word none
         *   source stretch  19..20  "."
         *   request stretch 11..21  " 1 (Fees)."
         *   shared prefix 0, shared suffix 1 (".")
         *   -> { startOffset 19, endOffset 19, replacementText " 1 (Fees)" }: Schedule{+ 1 (Fees)+}.
         */
        const punctuationKeptBeforeInsertedWords = (insertOpIndex: number, movedPunctuation: WordBeforeMovedPunctuation): PlannedTextEdit => {
            const insertOp = ops[insertOpIndex];
            /**
             * EXAMPLE
             *   insertOp.beforeStart = 4 -> 4 - 1 = 3, "Schedule.", the kept word right before the insertion
             *   insertOp.afterStart  = 2 -> 2 - 1 = 1, "Schedule", the request's spelling of that word
             */
            const sourceWordIndex = insertOp.beforeStart - 1;
            const requestWordIndex = insertOp.afterStart - 1;
            /**
             * EXAMPLE
             *   "Schedule." at 11, text before its punctuation "Schedule" (8) -> 11 + 8 = 19, the "."
             *   "Schedule"  at 3, text before its punctuation "Schedule" (8) -> 3 + 8 = 11, just after it
             */
            const sourceStretchStart = from[sourceWordIndex].startOffset + movedPunctuation.sourceTextBeforePunctuationLength;
            const requestStretchStart = to[requestWordIndex].startOffset + movedPunctuation.requestTextBeforePunctuationLength;
            /**
             * CASES
             *   - a source word follows the insertion point -> the stretch ends where it starts
             *   - the insertion closes the text             -> the stretch ends with the text
             */
            const sourceStretchEnd = insertOp.beforeStart < from.length ? from[insertOp.beforeStart].startOffset : sourceText.length;
            /**
             * CASES
             *   - a request word follows the inserted words -> the stretch ends where it starts
             *   - the inserted words close the text         -> the stretch ends with the text
             */
            const requestStretchEnd = insertOp.afterEnd < to.length ? to[insertOp.afterEnd].startOffset : requestText.length;
            const sourceStretch = sourceText.slice(sourceStretchStart, sourceStretchEnd);
            const requestStretch = requestText.slice(requestStretchStart, requestStretchEnd);
            const sharedPrefixLength = commonPrefixLength(sourceStretch, requestStretch);
            /**
             * EXAMPLE
             *   sourceStretch "." (1), requestStretch " 1 (Fees)." (10), shared prefix 0
             *   min(1, 10) - 0 = 1 caps the suffix at the one source character
             */
            const sharedSuffixLength = commonSuffixLength(sourceStretch, requestStretch, Math.min(sourceStretch.length, requestStretch.length) - sharedPrefixLength);
            return wordsEditOf(
                /**
                 * The insertion's own units: the empty run at the source unit after the
                 * kept word, and the request units it writes.
                 *
                 * WHY not the kept word as well
                 *   A caller that reads the plan in units pairs a run's source and request
                 *   units one for one. Named over the kept word, the edit read one source
                 *   unit against three, and the card editor struck "Schedule." whole and
                 *   wrote "Schedule" back.
                 *
                 * EXAMPLE
                 *   source "in the Fee Schedule." -> units [4, 4), after "Schedule."
                 *   request "in Schedule 1 (Fees)." -> units [2, 4), "1" and "(Fees)."
                 */
                insertOp,
                {
                    /**
                     * EXAMPLE
                     *   sourceStretchStart 19 + shared prefix 0 -> 19, in front of the "."
                     */
                    startOffset: sourceStretchStart + sharedPrefixLength,
                    /**
                     * EXAMPLE
                     *   sourceStretchEnd 20 - shared suffix 1 -> 19: the "." stays, nothing is struck
                     */
                    endOffset: sourceStretchEnd - sharedSuffixLength,
                    /**
                     * EXAMPLE
                     *   " 1 (Fees)." (10), slice(0, 10 - 1) -> " 1 (Fees)"
                     */
                    replacementText: requestStretch.slice(sharedPrefixLength, requestStretch.length - sharedSuffixLength),
                    /**
                     * EXAMPLE
                     *   requestStretchStart 11 + shared prefix 0 -> 11; requestStretchEnd 21 - shared suffix 1 -> 20: " 1 (Fees)" at 11..20
                     */
                    requestStartOffset: requestStretchStart + sharedPrefixLength,
                    requestEndOffset: requestStretchEnd - sharedSuffixLength,
                },
            );
        };

        /**
         * A kept word that takes the end punctuation of the words the request deletes
         * right after it: how long the word's text before its punctuation is in the
         * source and in the request, and which deleted unit carried the punctuation.
         */
        interface WordTakingDeletedPunctuation {
            sourceTextBeforePunctuationLength: number;
            requestTextBeforePunctuationLength: number;
            punctuatedDeletedUnitIndex: number;
        }
        /**
         * The equal run at `equalOpIndex`'s last word, when the request gives it the
         * end punctuation of the words it deletes right after it.
         *
         * WHEN the next hunk strikes source words, the run's last word ends no sentence
         * in the source and takes new end punctuation in the request, and the first
         * struck word that carries end punctuation ends with that same punctuation
         *   CONSEQUENCE
         *     the word: the punctuation never changed, the request deleted the words in
         *     front of it. Drawn as a punctuation edit plus a deletion, the redline
         *     inserted a period after the kept word and struck the old one with the
         *     words before it, in the same change as the next sentence's words: a
         *     reviewer accepting one sentence's edit and rejecting the other left
         *     "call. today." or "call I'd appreciate" (R16).
         *
         * EXAMPLE
         *   source "off the call today. I would be grateful for your"
         *   request "off today's call. I'd appreciate your"
         *   last word of the run   "call" -> "call."   gains "."
         *   struck words            "today.", "I", "would", "be", "grateful", "for"
         *   first with punctuation  "today.", which ends with "."                -> the word
         *   source "subject to fees, charges and taxes."   request "subject to fees."
         *   "fees," -> "fees.", first struck word with punctuation "taxes." -> the word
         *   source "Sam separately. As such, I"   request "Sam separately, so I"
         *   "separately." ends a sentence in the source                   -> null
         *   (`sentencesJoinedAfter` draws that one)
         *
         * NULL
         *   the hunk at `equalOpIndex` is not an equal run, the next hunk strikes no
         *   source word, the kept word ends a sentence in the source, or the punctuation
         *   really changes.
         */
        const deletedWordsBeforeKeptPunctuation = (equalOpIndex: number): WordTakingDeletedPunctuation | null => {
            const equalOp = ops[equalOpIndex];
            /**
             * EXAMPLE
             *   equalOpIndex = 2, the run "call" -> 2 + 1 = 3, the hunk right after the run
             */
            const nextOp = ops[equalOpIndex + 1];
            /**
             * WHEN the hunk is not an equal run, the run is the last hunk, or the next
             * hunk only inserts
             *   CONSEQUENCE
             *     null: no struck word follows a kept word.
             */
            if (equalOp.tag !== "equal" || nextOp === undefined || nextOp.beforeStart === nextOp.beforeEnd) return null;
            /**
             * EXAMPLE
             *   equalOp.beforeEnd = 3 -> 3 - 1 = 2, "call", the run's last source word
             *   equalOp.afterEnd  = 3 -> 3 - 1 = 2, "call.", the request word it was matched with
             */
            const sourceWordParts = WordTokenizer.splitTrailingPunctuation(from[equalOp.beforeEnd - 1].text);
            const requestWordParts = WordTokenizer.splitTrailingPunctuation(to[equalOp.afterEnd - 1].text);
            /**
             * WHEN either spelling of the kept word holds no letter or digit
             *   CONSEQUENCE
             *     null: it has no end punctuation to keep apart from its text.
             */
            if (sourceWordParts === null || requestWordParts === null) return null;
            const takenPunctuation = requestWordParts.trailingPunctuation;
            /**
             * CASES, every mark read under the fold
             *   - the request word ends with no punctuation         -> null: nothing to keep
             *   - it ends as the source word does                   -> null: no punctuation edit to fold
             *   - the source word ends a sentence                   -> null: the request joins that
             *     sentence to the next ("separately." -> "separately,"), drawn by `sentencesJoinedAfter`
             */
            if (takenPunctuation.length === 0 || punctuationReadsTheSame(sourceWordParts.trailingPunctuation, takenPunctuation)) return null;
            if (unitEndsSentence(from[equalOp.beforeEnd - 1])) return null;
            for (let struckUnitIndex = nextOp.beforeStart; struckUnitIndex < nextOp.beforeEnd; struckUnitIndex++) {
                const struckUnitParts = WordTokenizer.splitTrailingPunctuation(from[struckUnitIndex].text);
                /**
                 * WHEN the struck word carries no end punctuation
                 *   CONSEQUENCE
                 *     look at the next one: the sentence or clause it ends is still open
                 */
                if (struckUnitParts === null || struckUnitParts.trailingPunctuation.length === 0) continue;
                /**
                 * CASES, under the fold
                 *   - the first punctuated struck word ends with the kept word's new punctuation -> the word
                 *   - it ends with something else                                               -> null
                 */
                if (!WordTokenizer.comparisonKey(struckUnitParts.trailingPunctuation).endsWith(WordTokenizer.comparisonKey(takenPunctuation))) return null;
                return {
                    sourceTextBeforePunctuationLength: sourceWordParts.textBeforeTrailingPunctuation.length,
                    requestTextBeforePunctuationLength: requestWordParts.textBeforeTrailingPunctuation.length,
                    punctuatedDeletedUnitIndex: struckUnitIndex,
                };
            }
            return null;
        };
        /**
         * The edits for a kept word that takes the end punctuation of the words struck
         * after it (`deletedWordsBeforeKeptPunctuation`): one deletion of the words in
         * front of that punctuation, which stays standing, then the rest of the hunk, the
         * next sentence's words, as an edit of its own.
         *
         * WHY two edits
         *   Each sentence's change is one tracked change, so a reviewer can accept one
         *   and reject the other and read either sentence as it was or as edited.
         *
         * EXAMPLE
         *   source "off the call today. I would be grateful for your"    "call" at 8..12, "today." at 13..19
         *   request "off today's call. I'd appreciate your"              "call." at 12..17
         *   source stretch  12..19  " today."     request stretch  16..17  "."
         *   shared suffix 1 (".")  -> delete 12..18 " today": call{- today}.
         *   rest: "I would be grateful for" at 20..43 -> "I'd appreciate", its own edit
         */
        const punctuationKeptAfterDeletedWords = (hunkOpIndex: number, takenPunctuation: WordTakingDeletedPunctuation): PlannedTextEdit[] => {
            const hunkOp = ops[hunkOpIndex];
            /**
             * EXAMPLE
             *   hunkOp.beforeStart = 3 -> 3 - 1 = 2, "call", the kept word before the struck words
             *   hunkOp.afterStart  = 3 -> 3 - 1 = 2, "call.", the request's spelling of it
             */
            const sourceWordIndex = hunkOp.beforeStart - 1;
            const requestWordIndex = hunkOp.afterStart - 1;
            const punctuatedDeletedUnit = from[takenPunctuation.punctuatedDeletedUnitIndex];
            /**
             * EXAMPLE
             *   "call" at 8, text before its punctuation "call" (4)   -> 8 + 4 = 12, just after it
             *   "call." at 12, text before its punctuation "call" (4) -> 12 + 4 = 16, the "."
             */
            const sourceStretchStart = from[sourceWordIndex].startOffset + takenPunctuation.sourceTextBeforePunctuationLength;
            const requestStretchStart = to[requestWordIndex].startOffset + takenPunctuation.requestTextBeforePunctuationLength;
            const sourceStretch = sourceText.slice(sourceStretchStart, punctuatedDeletedUnit.endOffset);
            const requestStretch = requestText.slice(requestStretchStart, to[requestWordIndex].endOffset);
            const sharedPrefixLength = commonPrefixLength(sourceStretch, requestStretch);
            /**
             * EXAMPLE
             *   sourceStretch " today." (7), requestStretch "." (1), shared prefix 0
             *   min(7, 1) - 0 = 1 caps the suffix at the one request character
             */
            const sharedSuffixLength = commonSuffixLength(sourceStretch, requestStretch, Math.min(sourceStretch.length, requestStretch.length) - sharedPrefixLength);
            const keptPunctuationEdit = wordsEditOf(
                /**
                 * The struck words' own units, and the empty run after the kept word on
                 * the request side: the edit writes no request unit.
                 *
                 * WHY not the kept word as well
                 *   A caller that reads the plan in units pairs a run's source and request
                 *   units one for one; named over "call" too, the edit read two source
                 *   units against one and the card editor struck "call" whole.
                 *
                 * EXAMPLE
                 *   kept "call" at source unit 2 and request unit 2, struck "today." at 3
                 *   -> source 2 + 1 = 3 through 3 + 1 = 4: [3, 4); request 2 + 1 = 3: [3, 3)
                 */
                { beforeStart: sourceWordIndex + 1, beforeEnd: takenPunctuation.punctuatedDeletedUnitIndex + 1, afterStart: requestWordIndex + 1, afterEnd: requestWordIndex + 1 },
                {
                    /**
                     * EXAMPLE
                     *   sourceStretchStart 12 + shared prefix 0 -> 12, right after "call"
                     */
                    startOffset: sourceStretchStart + sharedPrefixLength,
                    /**
                     * EXAMPLE
                     *   "today." ends at 19, shared suffix 1 -> 18: the "." stays
                     */
                    endOffset: punctuatedDeletedUnit.endOffset - sharedSuffixLength,
                    /**
                     * EXAMPLE
                     *   "." (1), slice(0, 1 - 1) -> "": nothing is written, " today" is struck
                     */
                    replacementText: requestStretch.slice(sharedPrefixLength, requestStretch.length - sharedSuffixLength),
                    /**
                     * EXAMPLE
                     *   requestStretchStart 16 + shared prefix 0 -> 16; "call." ends at 17, shared suffix 1 -> 16: nothing sits at 16..16
                     */
                    requestStartOffset: requestStretchStart + sharedPrefixLength,
                    requestEndOffset: to[requestWordIndex].endOffset - sharedSuffixLength,
                },
            );
            /**
             * The hunk's words after the punctuated struck word: the next sentence's.
             *
             * EXAMPLE
             *   punctuatedDeletedUnitIndex 3 -> 3 + 1 = 4, "I", the first of them
             */
            const nextSentenceUnits = { beforeStart: takenPunctuation.punctuatedDeletedUnitIndex + 1, beforeEnd: hunkOp.beforeEnd, afterStart: hunkOp.afterStart, afterEnd: hunkOp.afterEnd };
            /**
             * WHEN the hunk struck nothing after that word and writes nothing
             *   CONSEQUENCE
             *     the deletion is the whole change: "the call today. The" -> "the call. The"
             */
            if (nextSentenceUnits.beforeStart === nextSentenceUnits.beforeEnd && nextSentenceUnits.afterStart === nextSentenceUnits.afterEnd) return [keptPunctuationEdit];
            /**
             * WHY the next sentence's edit may borrow the spacing on its left
             *   On both sides the text before it is kept: the source's "." after "today"
             *   and the request's "." after "call".
             *
             * EXAMPLE
             *   hunkOpIndex = 3 -> 3 + 1 = 4, the run "your" after this hunk
             */
            const growIntoTrailingGap = hunkOpIndex + 1 < ops.length && ops[hunkOpIndex + 1].tag === "equal";
            return [keptPunctuationEdit, wordsEditOf(nextSentenceUnits, unitRangeEdit(nextSentenceUnits, true, growIntoTrailingGap))];
        };

        /**
         * Whether the unit's end punctuation closes a sentence.
         *
         * EXAMPLE
         *   "separately." -> true     "separately," -> false     "—" -> false (no letter or digit)
         */
        const unitEndsSentence = (unit: WordToken): boolean => {
            const unitParts = WordTokenizer.splitTrailingPunctuation(unit.text);
            return unitParts !== null && WordTokenizer.endsSentence(unitParts.trailingPunctuation);
        };
        /**
         * A kept word whose sentence end the request turns into a clause mark: how
         * long the word's text before its punctuation is in the source and in the
         * request.
         */
        interface WordJoiningSentences {
            sourceTextBeforePunctuationLength: number;
            requestTextBeforePunctuationLength: number;
        }
        /**
         * The equal run at `equalOpIndex`'s last word, when the request joins the
         * sentence that word ends to the next one, rewriting the next sentence's
         * opening words.
         *
         * WHEN the run's last word ends a sentence in the source and not in the request,
         * the next hunk strikes source words, and none of the words it writes ends a
         * sentence
         *   CONSEQUENCE
         *     the word: the request merges the two sentences. Drawn as the word's
         *     punctuation change and the hunk apart, a reviewer accepting one and rejecting
         *     the other read "separately, As such, I" or "separately. As such, so I" (R16).
         *
         * EXAMPLE
         *   source "Sam separately. As such, I think"   request "Sam separately, so I think"
         *   "separately." ends a sentence, "separately," does not; the hunk "As such," -> "so"
         *   -> the word
         *   source "the Agreement."   request "the Agreement; and"
         *   the next hunk only inserts                                    -> null
         *
         * NULL
         *   the hunk at `equalOpIndex` is not an equal run, the next hunk strikes no
         *   source word, the source word ends no sentence or the request word still ends
         *   one, or the request writes a sentence end in the hunk.
         */
        const sentencesJoinedAfter = (equalOpIndex: number): WordJoiningSentences | null => {
            const equalOp = ops[equalOpIndex];
            /**
             * EXAMPLE
             *   equalOpIndex = 0, the run "Sam separately." -> 0 + 1 = 1, the hunk right after it
             */
            const nextOp = ops[equalOpIndex + 1];
            /**
             * WHEN the hunk is not an equal run, the run is the last hunk, or the next
             * hunk only inserts
             *   CONSEQUENCE
             *     null: no next sentence's words are rewritten after a kept word.
             */
            if (equalOp.tag !== "equal" || nextOp === undefined || nextOp.beforeStart === nextOp.beforeEnd) return null;
            /**
             * EXAMPLE
             *   equalOp.beforeEnd = 2 -> 2 - 1 = 1, "separately.", the run's last source word
             *   equalOp.afterEnd  = 2 -> 2 - 1 = 1, "separately,", the request word it was matched with
             */
            const sourceWord = from[equalOp.beforeEnd - 1];
            const requestWord = to[equalOp.afterEnd - 1];
            const sourceWordParts = WordTokenizer.splitTrailingPunctuation(sourceWord.text);
            const requestWordParts = WordTokenizer.splitTrailingPunctuation(requestWord.text);
            /**
             * CASES
             *   - either spelling holds no letter or digit -> null
             *   - the source word ends no sentence         -> null: no sentence end to join across
             *   - the request word still ends a sentence   -> null: the sentences stay apart
             */
            if (sourceWordParts === null || requestWordParts === null) return null;
            if (!unitEndsSentence(sourceWord) || unitEndsSentence(requestWord)) return null;
            for (let writtenUnitIndex = nextOp.afterStart; writtenUnitIndex < nextOp.afterEnd; writtenUnitIndex++) {
                /**
                 * WHEN a word the hunk writes ends a sentence
                 *   CONSEQUENCE
                 *     null: the request closes a sentence there, so it does not simply join two
                 */
                if (unitEndsSentence(to[writtenUnitIndex])) return null;
            }
            return {
                sourceTextBeforePunctuationLength: sourceWordParts.textBeforeTrailingPunctuation.length,
                requestTextBeforePunctuationLength: requestWordParts.textBeforeTrailingPunctuation.length,
            };
        };
        /**
         * How many characters two texts share at their start, counting only punctuation
         * and spacing: the count stops at the first letter or digit, so no word is
         * shared between what is struck and what is written.
         *
         * EXAMPLE
         *   ". As such, " and ", so " -> 0: "." and "," differ
         *   ", as " and ", so "       -> 2: ", " is shared, the "a" and "s" are letters
         */
        const sharedPunctuationPrefixLength = (left: string, right: string): number => {
            let length = 0;
            /**
             * CASES
             *   - either text is used up                     -> stop
             *   - the characters differ under the fold       -> stop
             *   - the shared character is a letter or digit  -> stop: words are never shared
             */
            while (length < left.length && length < right.length && charactersReadTheSame(left[length], right[length]) && !WordTokenizer.holdsALetterOrDigit(left[length])) length++;
            return length;
        };
        /**
         * How many characters two texts share at their end, counting only punctuation
         * and spacing, never more than `limit`.
         *
         * EXAMPLE
         *   ". As such, " and ", so " -> 1: " " is shared; "," and "o" differ
         *   left.length 11, length 0 -> left[11 - 1 - 0] = left[10], the last character
         */
        const sharedPunctuationSuffixLength = (left: string, right: string, limit: number): number => {
            let length = 0;
            /**
             * CASES
             *   - the count has reached `limit`               -> stop: it would overlap the prefix
             *   - the characters differ under the fold        -> stop
             *   - the shared character is a letter or digit   -> stop
             */
            while (
                length < limit &&
                charactersReadTheSame(left[left.length - 1 - length], right[right.length - 1 - length]) &&
                !WordTokenizer.holdsALetterOrDigit(left[left.length - 1 - length])
            ) length++;
            return length;
        };
        /**
         * The one edit for a kept word whose sentence end the request turns into a
         * clause mark (`sentencesJoinedAfter`): the stretch from the word's punctuation to
         * the next kept word, rewritten as the request spells it, with the punctuation and
         * spacing both sides share at either end left standing.
         *
         * WHY one edit
         *   The punctuation change and the next sentence's new opening are one wording;
         *   either alone reads as neither sentence.
         *
         * EXAMPLE
         *   source "Sam separately. As such, I think"   "separately." at 4..15, "I" at 25
         *   request "Sam separately, so I think"        "separately," at 4..15, "I" at 19
         *   source stretch  14..25  ". As such, "   request stretch  14..19  ", so "
         *   shared prefix 0, shared suffix 1 (" ")
         *   -> { startOffset 14, endOffset 24, replacementText ", so" }: separately{-. As such,-}{+, so+} I
         */
        const sentencesJoinedEdit = (hunkOpIndex: number, joiningWord: WordJoiningSentences): PlannedTextEdit => {
            const hunkOp = ops[hunkOpIndex];
            /**
             * EXAMPLE
             *   hunkOp.beforeStart = 2 -> 2 - 1 = 1, "separately.", the kept word before the hunk
             *   hunkOp.afterStart  = 2 -> 2 - 1 = 1, "separately,", the request's spelling of it
             */
            const sourceWord = from[hunkOp.beforeStart - 1];
            const requestWord = to[hunkOp.afterStart - 1];
            /**
             * EXAMPLE
             *   "separately." at 4, text before its punctuation "separately" (10) -> 4 + 10 = 14, the "."
             *   "separately," at 4, text before its punctuation "separately" (10) -> 4 + 10 = 14, the ","
             */
            const sourceStretchStart = sourceWord.startOffset + joiningWord.sourceTextBeforePunctuationLength;
            const requestStretchStart = requestWord.startOffset + joiningWord.requestTextBeforePunctuationLength;
            /**
             * CASES
             *   - a kept word follows the hunk -> the stretch ends where it starts ("I" at 25, at 19)
             *   - the hunk closes the text     -> the stretch ends with the text
             */
            const sourceStretchEnd = hunkOp.beforeEnd < from.length ? from[hunkOp.beforeEnd].startOffset : sourceText.length;
            const requestStretchEnd = hunkOp.afterEnd < to.length ? to[hunkOp.afterEnd].startOffset : requestText.length;
            const sourceStretch = sourceText.slice(sourceStretchStart, sourceStretchEnd);
            const requestStretch = requestText.slice(requestStretchStart, requestStretchEnd);
            const sharedPrefixLength = sharedPunctuationPrefixLength(sourceStretch, requestStretch);
            /**
             * EXAMPLE
             *   sourceStretch ". As such, " (11), requestStretch ", so " (5), shared prefix 0
             *   min(11, 5) - 0 = 5 caps the suffix, so it never overlaps the prefix
             */
            const sharedSuffixLength = sharedPunctuationSuffixLength(sourceStretch, requestStretch, Math.min(sourceStretch.length, requestStretch.length) - sharedPrefixLength);
            return wordsEditOf(
                /**
                 * The hunk's own units: the kept word stays paired with its request
                 * spelling for a caller that reads the plan in units, as in the card editor.
                 *
                 * EXAMPLE
                 *   source "As", "such," -> units [2, 4); request "so" -> units [2, 3)
                 */
                hunkOp,
                {
                    /**
                     * EXAMPLE
                     *   sourceStretchStart 14 + shared prefix 0 -> 14, the "."
                     */
                    startOffset: sourceStretchStart + sharedPrefixLength,
                    /**
                     * EXAMPLE
                     *   sourceStretchEnd 25 - shared suffix 1 -> 24, just after "such,"
                     */
                    endOffset: sourceStretchEnd - sharedSuffixLength,
                    /**
                     * EXAMPLE
                     *   ", so " (5), slice(0, 5 - 1) -> ", so"
                     */
                    replacementText: requestStretch.slice(sharedPrefixLength, requestStretch.length - sharedSuffixLength),
                    /**
                     * EXAMPLE
                     *   requestStretchStart 14 + shared prefix 0 -> 14; requestStretchEnd 19 - shared suffix 1 -> 18: ", so" at 14..18
                     */
                    requestStartOffset: requestStretchStart + sharedPrefixLength,
                    requestEndOffset: requestStretchEnd - sharedSuffixLength,
                },
            );
        };

        const edits: PlannedTextEdit[] = [];
        ops.forEach((op, index) => {
            /**
             * WHEN the diff hunk is `equal`
             *   CONSEQUENCE
             *     Draw the punctuation and the spacing the request changes inside the run
             *     instead of planning a word replacement.
             *
             * WHY a run the diff calls equal can still need marking
             *   The diff matches words by `diffKeyOf`, which stops at a word's last letter
             *   or digit, so a run's words can still differ in the punctuation after it;
             *   the whitespace between them can differ too.
             */
            if (op.tag === "equal") {
                edits.push(...equalRunEdits(op, wordBeforeMovedPunctuation(index) !== null || deletedWordsBeforeKeptPunctuation(index) !== null || sentencesJoinedAfter(index) !== null));
                return;
            }
            /**
             * CASES
             *   - the first hunk                -> no run before it, so no moved punctuation
             *   - any later hunk, index 3 say   -> 3 - 1 = 2, the hunk right before it
             */
            const movedPunctuation = index > 0 ? wordBeforeMovedPunctuation(index - 1) : null;
            const takenPunctuation = index > 0 ? deletedWordsBeforeKeptPunctuation(index - 1) : null;
            const joiningWord = index > 0 ? sentencesJoinedAfter(index - 1) : null;
            /**
             * CASES
             *   - this hunk inserts words in front of the punctuation the run before it keeps
             *     -> one edit writes the words before that punctuation and leaves it standing
             *   - this hunk strikes the words between the run's last word and the punctuation
             *     that word takes -> one deletion in front of that punctuation, then the rest
             *     of the hunk as its own edit
             *   - this hunk rewrites the opening of the sentence the run's last word now joins
             *     -> one edit from that word's punctuation through the hunk
             *   - any other hunk -> its words, struck and inserted whole
             */
            if (movedPunctuation !== null) edits.push(punctuationKeptBeforeInsertedWords(index, movedPunctuation));
            else if (takenPunctuation !== null) edits.push(...punctuationKeptAfterDeletedWords(index, takenPunctuation));
            else if (joiningWord !== null) edits.push(sentencesJoinedEdit(index, joiningWord));
            else edits.push(wordsEditOf(op, hunkEdit(index)));
        });
        return edits;
    }

    /**
     * The change between `sourceText` and `requestText` as pieces: the same plan as
     * `editsBetween`, with the stretches the edits leave standing between them, so a
     * caller that draws the change (the card editor) reads what the engine writes and
     * never derives its own picture of it.
     *
     * RETURNS
     *   Pieces in order. The kept and deleted pieces' `text`s joined are `sourceText`;
     *   the kept pieces' request spans and the inserted pieces' `text`s, in order, cover
     *   `requestText`. A request that writes nothing is one kept piece over both texts.
     *
     * EXAMPLE
     *   source "as set out in the Fee Schedule."   request "as set out in Schedule 1 (Fees)."
     *   edits   strike "the Fee " at 14..22; write " 1 (Fees)" at 30 (before the "."), from 22..31 of the request
     *   pieces  kept "as set out in " 0..14 | 0..14
     *           deleted "the Fee "    14..22 | at 14
     *           kept "Schedule"       22..30 | 14..22
     *           inserted " 1 (Fees)"  at 30  | 22..31
     *           kept "."              30..31 | 31..32
     */
    static piecesBetween(sourceText: string, requestText: string, options: TextEditPlanOptions = {}): ChangePiece[] {
        const pieces: ChangePiece[] = [];
        let sourceCursor = 0;
        let requestCursor = 0;
        /**
         * The stretch both texts hold from the cursors up to the next edit, when either
         * side has characters there; a run of whitespace folded to one space can leave
         * the two sides unequal in length.
         */
        const keepUpTo = (sourceEndOffset: number, requestEndOffset: number): void => {
            if (sourceEndOffset > sourceCursor || requestEndOffset > requestCursor) {
                pieces.push({ kind: "kept", text: sourceText.slice(sourceCursor, sourceEndOffset), sourceStartOffset: sourceCursor, sourceEndOffset, requestStartOffset: requestCursor, requestEndOffset });
            }
            sourceCursor = sourceEndOffset;
            requestCursor = requestEndOffset;
        };
        for (const edit of TextEditPlanner.editsBetween(sourceText, requestText, options)) {
            keepUpTo(edit.startOffset, edit.requestStartOffset);
            /** WHEN the edit strikes characters CONSEQUENCE a deleted piece, sitting in the request where the edit writes. */
            if (edit.endOffset > edit.startOffset) {
                pieces.push({ kind: "deleted", text: sourceText.slice(edit.startOffset, edit.endOffset), sourceStartOffset: edit.startOffset, sourceEndOffset: edit.endOffset, requestStartOffset: edit.requestStartOffset, requestEndOffset: edit.requestStartOffset });
            }
            /** WHEN the edit writes characters CONSEQUENCE an inserted piece, written in the source after whatever the edit struck. */
            if (edit.replacementText.length > 0) {
                pieces.push({ kind: "inserted", text: edit.replacementText, sourceStartOffset: edit.endOffset, sourceEndOffset: edit.endOffset, requestStartOffset: edit.requestStartOffset, requestEndOffset: edit.requestEndOffset });
            }
            sourceCursor = edit.endOffset;
            requestCursor = edit.requestEndOffset;
        }
        keepUpTo(sourceText.length, requestText.length);
        return pieces;
    }
}
